import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { supabase, getAuthHeader } from '@/services/supabase';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import { TierBadge, StatusBadge } from '@/components/common/Badge';
import { PageSpinner } from '@/components/common/Spinner';
import { formatDate, formatCurrency } from '@/utils/formatters';
import toast from 'react-hot-toast';

export default function BusinessDetailPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showUnclaimModal, setShowUnclaimModal] = useState(false);
  const [unclaimReason, setUnclaimReason] = useState('');

  const { data: business, isLoading } = useQuery({
    queryKey: ['business', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_profiles')
        .select(`
          *,
          business_subscriptions(*),
          owner:user_profiles!user_id(full_name, email)
        `)
        .eq('business_id', businessId)
        .single();

      if (error) throw error;
      return {
        ...data,
        subscription: data.business_subscriptions?.[0] || null,
      };
    },
    enabled: !!businessId,
  });

  // Unclaim profile mutation
  const unclaimMutation = useMutation({
    mutationFn: async ({ businessId, reason }: { businessId: string; reason?: string }) => {
      const authHeader = await getAuthHeader();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-unclaim-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader,
          },
          body: JSON.stringify({ business_id: businessId, reason }),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to unclaim profile');
      }
      return result;
    },
    onSuccess: () => {
      toast.success('Profile unclaimed successfully');
      setShowUnclaimModal(false);
      setUnclaimReason('');
      queryClient.invalidateQueries({ queryKey: ['business', businessId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unclaim profile');
    },
  });

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!business) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Business not found</p>
        <Button onClick={() => navigate('/businesses')} className="mt-4">
          Back to Businesses
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate('/businesses')}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Businesses
      </button>

      {/* Business header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {business.logo_url ? (
            <img
              src={business.logo_url}
              alt={business.business_name || business.name}
              className="w-20 h-20 rounded-xl object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-gray-200 flex items-center justify-center text-3xl font-bold text-gray-600">
              {(business.business_name || business.name || '?')[0].toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{business.business_name || business.name}</h1>
            <p className="text-gray-500">{business.industry || 'No industry'}</p>
            <div className="mt-2 flex items-center gap-2">
              <TierBadge tier={business.subscription_type || business.tier} />
              <StatusBadge status={business.business_status || business.moderation_status || 'pending'} />
              {business.claim_status && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  business.claim_status === 'claimed'
                    ? 'bg-green-100 text-green-700'
                    : business.claim_status === 'unclaimed'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {business.claim_status === 'claimed' ? 'Claimed' : 'Unclaimed'}
                </span>
              )}
              {business.is_verified && (
                <span className="text-green-600 text-sm">✓ Verified</span>
              )}
            </div>
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex gap-2">
          {business.claim_status === 'claimed' && (
            <Button
              variant="danger"
              onClick={() => setShowUnclaimModal(true)}
            >
              Unclaim Profile
            </Button>
          )}
        </div>
      </div>

      {/* Unclaim Confirmation Modal */}
      {showUnclaimModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 text-amber-600 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6" />
              <h3 className="text-lg font-semibold">Unclaim Business Profile</h3>
            </div>
            <p className="text-gray-600 mb-4">
              This will remove the association between this business profile and its current owner. The profile will be reverted to an unclaimed preset state:
            </p>
            <ul className="text-sm text-gray-500 mb-4 list-disc list-inside space-y-1">
              <li>Owner will be removed (user_id = null)</li>
              <li>Status will change to "Inactive"</li>
              <li>Subscription type will revert to "preset"</li>
              <li>Profile will no longer appear in consumer searches</li>
              <li>A new claim code can be generated if needed</li>
            </ul>
            <div className="mb-4">
              <label htmlFor="unclaim-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <textarea
                id="unclaim-reason"
                name="unclaimReason"
                value={unclaimReason}
                onChange={(e) => setUnclaimReason(e.target.value)}
                placeholder="Enter reason for unclaiming..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                rows={3}
                aria-label="Reason for unclaiming"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowUnclaimModal(false);
                  setUnclaimReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => unclaimMutation.mutate({
                  businessId: business.business_id,
                  reason: unclaimReason || undefined
                })}
                disabled={unclaimMutation.isPending}
              >
                {unclaimMutation.isPending ? 'Unclaiming...' : 'Confirm Unclaim'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Business Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <h2 className="text-lg font-semibold">Business Information</h2>
          </CardHeader>
          <CardBody>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-gray-900">{business.business_name || business.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Industry</dt>
                <dd className="mt-1 text-gray-900">{business.industry || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Owner</dt>
                <dd className="mt-1 text-gray-900">
                  {business.owner?.full_name || business.owner?.email || (business.user_id ? 'User ID: ' + business.user_id : 'No owner')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Contact Email</dt>
                <dd className="mt-1 text-gray-900">{business.contact_email || business.email || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="mt-1 text-gray-900">{business.phone || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Website</dt>
                <dd className="mt-1 text-gray-900">
                  {business.website ? (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                      {business.website}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Location</dt>
                <dd className="mt-1 text-gray-900">
                  {business.city || business.state ? `${business.city || ''}${business.city && business.state ? ', ' : ''}${business.state || ''}` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-gray-900">{formatDate(business.created_at)}</dd>
              </div>
              {business.claimed_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Claimed At</dt>
                  <dd className="mt-1 text-gray-900">{formatDate(business.claimed_at)}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Subscription Type</dt>
                <dd className="mt-1 text-gray-900 capitalize">{business.subscription_type || '-'}</dd>
              </div>
            </dl>
            {business.description && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-gray-900">{business.description}</dd>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Subscription Info */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Subscription</h2>
          </CardHeader>
          <CardBody>
            {business.subscription ? (
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <StatusBadge status={business.subscription.status} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Plan Amount</dt>
                  <dd className="mt-1 text-gray-900">
                    {formatCurrency(business.subscription.plan_amount_cents)}/month
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Current Period</dt>
                  <dd className="mt-1 text-gray-900">
                    {formatDate(business.subscription.current_period_start)} - {formatDate(business.subscription.current_period_end)}
                  </dd>
                </div>
                {business.subscription.is_comped && (
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-700">
                      <strong>Complimentary:</strong> {business.subscription.comp_reason || 'No reason specified'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No active subscription</p>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
